import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { Router } from "@angular/router";
import { HttpService } from "core/http.service";
import { MainService } from "core/main.service";
import { NotificationService } from "core/notification.service";
import { lastValueFrom, Observable, Subject } from "rxjs";
import { LanguageService } from "core/translate/language.service";
import { State } from "core/volatile-application-state.model";

declare interface LogoutResult {
	externalLogoutUrl?: string;
}

@Injectable({ providedIn: "root" })
export class AuthenticationService {
	private readonly httpService = inject(HttpService);
	private readonly httpClient = inject(HttpClient);
	private readonly router = inject(Router);
	private readonly mainService = inject(MainService);
	private readonly notificationService = inject(NotificationService);
	private readonly languageService = inject(LanguageService);

	private parallelAuth: Promise<void> | null = null;

	private _triggerInvalidateCaches = new Subject<void>();

	get invalidateCaches$(): Observable<void> {
		return this._triggerInvalidateCaches.asObservable();
	}

	/**
	 * Tries to authenticate the user based on the provided username and password.
	 *
	 * @param username
	 *            Username.
	 * @param password
	 *            Password.
	 * @param rememberUser
	 *            If set to true, a cookie will be stored, which enables the user to stay authenticated, until he/she explicitly signs out.
	 * @param captchaText
	 *            Needed if the account has been locked due to too many failed login attempts.
	 * @param captchaToken
	 * 					  Needed alongside the captchaText, token has been returned by the last login error response.
	 * @return promise
	 */
	async passwordAuthentication(
		username: string,
		password: string,
		rememberUser: boolean,
		captchaText?: string,
		captchaToken?: string,
		securityCode?: string,
	): Promise<void> {
		const params: pkit.auth.AuthenticationCredentials = {
			password: password,
			rememberUser: !!rememberUser,
			username: username,
			captchaText: captchaText ?? null,
			captchaToken: captchaToken ?? null,
			securityCode: securityCode ?? null,
			sudo: false,
		};
		const promise = this.httpService.post("rest/authentication/password", params);
		return promise.then((response) => {
			this.invalidateAllCaches();
			this.notificationService.init();
			return response;
		});
	}

	/**
	 * Tries to authenticate the stages user automatically (e.g. Spnego, Cookie etc.)
	 *
	 * @return Promise for the asynchronous XHR or NULL, if there is another request pending.
	 */
	async autoAuthentication(manualAnonymous = false): Promise<void> {
		if (!this.parallelAuth) {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Remove this comment and fix warnings!
			const params: any = {};
			if (manualAnonymous) {
				params.manualAnonymous = manualAnonymous;
			}
			const promise = this.httpService.get("rest/authentication/auto", params);
			this.parallelAuth = promise.then(
				(response) => {
					this.parallelAuth = null;
					this.invalidateAllCaches();
					this.notificationService.init();
					return response;
				},
				(response) => {
					this.parallelAuth = null;
					throw response;
				},
			);
			return this.parallelAuth;
		}
		return this.parallelAuth;
	}

	async logout(redirectToQueryParam: string | null): Promise<void> {
		try {
			const res = await lastValueFrom(
				this.httpClient.request("DELETE", "rest/authentication/logout", {
					observe: "response",
				}),
			);
			const logoutResult = res.body as LogoutResult;
			if (logoutResult.externalLogoutUrl) {
				await this.languageService.determineAndApplyLanguage("_vv", "BROWSER");
				window.location.href = logoutResult.externalLogoutUrl;
			} else {
				void this.router.navigate(["login"], {
					queryParams: {
						redirectTo: redirectToQueryParam,
					},
				});
			}
		} finally {
			this.notificationService.destroy();
			this.invalidateAllCaches();
		}
	}

	/**
	 * To be used only for special cases, like auto Kerberos (SPNEGO) reauthentication.
	 */
	invalidateAllCaches(): void {
		this.httpService.invalidateAll();
		this.mainService.applicationState.navigationDrawerOpen = State.RESPONSIVE;
		this._triggerInvalidateCaches.next();
	}

	needsAuthentication(errorResponse: unknown): boolean {
		if (errorResponse instanceof HttpErrorResponse) {
			switch (errorResponse.status) {
				case 401: {
					return true;
				}
				case 403: {
					// return true for 403, if not a stages exception.
					return errorResponse.error ? errorResponse.error.stagesExceptionType === undefined : true;
				}
				default: {
					return false;
				}
			}
		}
		return false;
	}

	async startSudo(
		username: string,
		password: string,
		sso: boolean,
		processVersion: string,
		captchaText?: string,
		captchaToken?: string,
		securityCode?: string,
	): Promise<void> {
		const params: pkit.auth.AuthenticationCredentials = {
			password: password,
			rememberUser: false,
			username: username,
			captchaText: captchaText ?? null,
			captchaToken: captchaToken ?? null,
			securityCode: securityCode ?? null,
			sudo: true,
		};
		if (sso) {
			return this.httpService.get("rest/authentication/auto", {
				forceAuthn: true,
				redirectTo: await this.mainService.getHomeUrl(processVersion),
			});
		}
		return this.httpService.post("rest/authentication/password", params);
	}

	async quitSudo(): Promise<void> {
		return this.httpService.del("rest/authentication/quitsudo");
	}
}
